import { useEffect, useState } from 'react';
import { SelectOption } from 'src/shared/components/select/Select';
import { availableAreaColors } from 'src/shared/constants/colors';
import { HabitatInsightsMetricType, HabitatInsightsProcessingStatus, Site } from 'src/shared/types';
import useProjectFilters from 'src/shared/hooks/useProjectFilters';
import { useProjectSitesQuery } from 'src/app/projects/views/project-list/state/api/projectListGraphSlice';
import { findLayers } from '../chartDataStatus';

const useHabitatMiniMap = () => {
    const { currentProjectFilters, currentProjectId } = useProjectFilters();

    const { currentData: projectSites } = useProjectSitesQuery(
        {
            projectId: currentProjectId || '',
        },
        { skip: !currentProjectId }
    );

    const [siteOptions, setSiteOptions] = useState<SelectOption[]>([]);
    const [selectedSites, setSelectedSites] = useState<string[]>([]);
    const [year, setYear] = useState<string | number>('none');
    const [shouldShowBufferArea] = useState<boolean>(false);

    const [filteredSites, setFilteredSites] = useState<Site[]>([]);
    const [activeLayerUrls, setActiveLayerUrls] = useState<string[]>([]);

    // Derive site options from project filters and set default selected sites on first load
    useEffect(() => {
        if (!projectSites || !currentProjectFilters?.habitatInsightsSiteOptions) {
            return;
        }

        const options = currentProjectFilters.habitatInsightsSiteOptions.sort((a, b) =>
            ((a.label as string) || '').localeCompare(b.label as string)
        );
        setSiteOptions(options);

        if (selectedSites.length === 0) {
            setSelectedSites(options.map(entry => String(entry.value)));
        }
    }, [projectSites, currentProjectFilters?.habitatInsightsSiteOptions]);

    // Compute filtered sites locally based on selectedSites
    useEffect(() => {
        if (!projectSites) {
            setFilteredSites([]);
            return;
        }

        const filtered = projectSites
            .filter(site => site.properties.name && selectedSites.includes(site.properties.name))
            .sort((a, b) => (a.properties.name as string).localeCompare(b.properties.name as string));
        setFilteredSites(filtered);
    }, [projectSites, selectedSites]);

    // Default to Land Cover latest year if metric/year are not set
    useEffect(() => {
        const availableYears = (projectSites || [])
            .flatMap(site => site.properties.metrics || [])
            .filter(m => m.metric === HabitatInsightsMetricType.LAND_COVER)
            .flatMap(m => m.layers)
            .filter(l => l.status === HabitatInsightsProcessingStatus.READY)
            .map(l => l.year);

        const latestYear = [...availableYears].sort((a, b) => Number(b) - Number(a))[0];

        setYear(prev => (prev === 'none' ? latestYear || 'none' : prev));
    }, [projectSites]);

    // Resolve active layer URLs for overlays
    useEffect(() => {
        if (!filteredSites.length) {
            setActiveLayerUrls([]);
            return;
        }

        const newLayerUrls = filteredSites
            .flatMap(site => findLayers(site.properties.metrics, HabitatInsightsMetricType.LAND_COVER, year))
            .filter(layer => layer.status === HabitatInsightsProcessingStatus.READY)
            .flatMap(layer => (shouldShowBufferArea ? [layer.url, layer.boundaryUrl] : [layer.url]))
            .filter(Boolean) as string[];

        setActiveLayerUrls(newLayerUrls);
    }, [filteredSites, year, shouldShowBufferArea]);

    const getColourForSite = (area: string) => {
        const siteIndex = siteOptions.findIndex(entry => entry.value === area);
        if (siteIndex === -1) {
            return '#000';
        }
        return availableAreaColors[siteIndex || 0];
    };

    return {
        filteredSites,
        activeLayerUrls,
        getColourForSite,
    };
};

export default useHabitatMiniMap;
